// Small colour maths helpers used to derive an accessible palette from a
// school's `themeColor`. Pure functions, no DOM access.

const clampChannel = (value) => Math.min(255, Math.max(0, Math.round(value)));

const HEX_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB_PATTERN = /^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})/i;

/**
 * Parse a colour string into { r, g, b }. Supports #rgb, #rgba, #rrggbb,
 * #rrggbbaa (alpha ignored) and rgb()/rgba(). Returns null when unusable.
 */
export const parseColor = (value) => {
  if (typeof value !== 'string') return null;

  const input = value.trim();
  if (!input) return null;

  const rgbMatch = input.match(RGB_PATTERN);
  if (rgbMatch) {
    const [, r, g, b] = rgbMatch;
    if ([r, g, b].some((part) => Number(part) > 255)) return null;
    return { r: Number(r), g: Number(g), b: Number(b) };
  }

  const hexMatch = input.match(HEX_PATTERN);
  if (!hexMatch) return null;

  let hex = hexMatch[1];
  if (hex.length === 3 || hex.length === 4) {
    hex = hex
      .slice(0, 3)
      .split('')
      .map((character) => character + character)
      .join('');
  }
  hex = hex.slice(0, 6);

  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
};

export const toHex = ({ r, g, b }) =>
  `#${[r, g, b]
    .map((channel) => clampChannel(channel).toString(16).padStart(2, '0'))
    .join('')}`;

/** Normalize any supported colour string to `#rrggbb`, or null when invalid. */
export const normalizeColor = (value) => {
  const rgb = parseColor(value);
  return rgb ? toHex(rgb) : null;
};

export const toRgbaString = ({ r, g, b }, alpha) => `rgba(${r}, ${g}, ${b}, ${alpha})`;

/** WCAG relative luminance (0 = black, 1 = white). */
export const relativeLuminance = ({ r, g, b }) => {
  const [red, green, blue] = [r, g, b].map((channel) => {
    const ratio = channel / 255;
    return ratio <= 0.03928 ? ratio / 12.92 : ((ratio + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

/** WCAG contrast ratio between two colours (1 – 21). */
export const contrastRatio = (colorA, colorB) => {
  const a = relativeLuminance(colorA);
  const b = relativeLuminance(colorB);
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);

  return (lighter + 0.05) / (darker + 0.05);
};

/** Blend `color` towards `target` by `weight` (0 = unchanged, 1 = target). */
export const mixColors = (color, target, weight) => {
  const amount = Math.min(1, Math.max(0, weight));

  return {
    r: clampChannel(color.r + (target.r - color.r) * amount),
    g: clampChannel(color.g + (target.g - color.g) * amount),
    b: clampChannel(color.b + (target.b - color.b) * amount),
  };
};

export const BLACK = { r: 0, g: 0, b: 0 };
export const WHITE = { r: 255, g: 255, b: 255 };

export const darken = (color, amount) => mixColors(color, BLACK, amount);
export const lighten = (color, amount) => mixColors(color, WHITE, amount);

const STEP = 0.06;
const MAX_STEPS = 18;

/**
 * Darken a colour until it meets `minRatio` against `against` (usually white),
 * so brand colours stay usable for text, icons and dark fills.
 */
export const darkenUntilContrast = (color, against, minRatio) => {
  let result = color;

  for (let step = 0; step < MAX_STEPS; step += 1) {
    if (contrastRatio(result, against) >= minRatio) break;
    result = mixColors(result, BLACK, STEP);
  }

  return result;
};

/** Lighten a colour until it meets `minRatio` against `against` (usually dark ink). */
export const lightenUntilContrast = (color, against, minRatio) => {
  let result = color;

  for (let step = 0; step < MAX_STEPS; step += 1) {
    if (contrastRatio(result, against) >= minRatio) break;
    result = mixColors(result, WHITE, STEP);
  }

  return result;
};

export const DARK_INK = { r: 16, g: 24, b: 40 };

/**
 * Pick whichever of two text colours reads best on `background`, preferring
 * `light` once it clears `minRatio` so filled brand surfaces keep white text.
 */
export const pickTextColor = (background, light = WHITE, dark = DARK_INK, minRatio = 4.5) => {
  const lightRatio = contrastRatio(background, light);
  if (lightRatio >= minRatio) return light;

  const darkRatio = contrastRatio(background, dark);
  if (darkRatio >= minRatio) return dark;

  // Neither is ideal (mid-tone brand colour): use the stronger of the two.
  return darkRatio >= lightRatio ? dark : light;
};

/**
 * Find a fill/text pair that clears `minRatio`.
 *
 * Mid-tone brand colours (a vivid pink or teal, say) can be too dark for black
 * text and too light for white text at the same time. In that case the fill is
 * nudged along the direction that is already closest, which keeps the hue while
 * making the pairing accessible.
 */
export const resolveAccessibleFill = (color, minRatio = 4.5) => {
  const lightRatio = contrastRatio(color, WHITE);
  const darkRatio = contrastRatio(color, DARK_INK);

  if (lightRatio >= minRatio) return { fill: color, text: WHITE, adjusted: false };
  if (darkRatio >= minRatio) return { fill: color, text: DARK_INK, adjusted: false };

  if (lightRatio >= darkRatio) {
    return {
      fill: darkenUntilContrast(color, WHITE, minRatio),
      text: WHITE,
      adjusted: true,
    };
  }

  return {
    fill: lightenUntilContrast(color, DARK_INK, minRatio),
    text: DARK_INK,
    adjusted: true,
  };
};

/** True when a colour is dark enough that darkening it further reads as flat. */
export const isVeryDark = (color) => relativeLuminance(color) < 0.06;
